import { Helmet } from "react-helmet-async";
import { Link } from "wouter";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Newspaper, BookOpen, Play, Package, Building2, Users,
  Lightbulb, BookMarked, Globe, Calendar, Mic, FileText,
  MonitorPlay, ArrowRight, Clock,
} from "lucide-react";

interface ContentType {
  icon: React.ElementType;
  title: string;
  description: string;
  href: string;
  color: string;
  available: true;
}

interface ComingSoon {
  icon: React.ElementType;
  title: string;
  description: string;
  available: false;
}

type CardDef = ContentType | ComingSoon;

const CONTENT_TYPES: CardDef[] = [
  {
    icon: Newspaper,
    title: "News & Articles",
    description: "Breaking news, in-depth features, technical analysis, and industry commentary across mechanical, electrical, and electronic engineering.",
    href: "/articles",
    color: "text-blue-500",
    available: true,
  },
  {
    icon: BookOpen,
    title: "Digital Magazine",
    description: "Our flagship publication in digital format — beautifully laid out issues covering the full bill of materials, available to read in-browser.",
    href: "/magazine",
    color: "text-violet-500",
    available: true,
  },
  {
    icon: Play,
    title: "Video Content",
    description: "Interviews with industry leaders, product walkthroughs, technology explainers, and event recordings — all in one place.",
    href: "/videos",
    color: "text-red-500",
    available: true,
  },
  {
    icon: Package,
    title: "Products & Action Cards",
    description: "Curated product cards from the world's leading manufacturers — specifications, use cases, and direct links to more information.",
    href: "/products",
    color: "text-amber-500",
    available: true,
  },
  {
    icon: Building2,
    title: "Companies Directory",
    description: "Profiles for the companies shaping the future of manufacturing — from global distributors to specialist component makers.",
    href: "/companies",
    color: "text-emerald-500",
    available: true,
  },
  {
    icon: Users,
    title: "People Directory",
    description: "The people behind the industry — engineers, executives, founders, and thought leaders, with their affiliations and content.",
    href: "/people",
    color: "text-sky-500",
    available: true,
  },
  {
    icon: Lightbulb,
    title: "Insights",
    description: "Long-form analytical pieces and expert commentary on market trends, technology shifts, and the forces reshaping manufacturing.",
    href: "/articles",
    color: "text-orange-500",
    available: true,
  },
  {
    icon: BookMarked,
    title: "Glossary",
    description: "Definitions and explanations of the technical terms, standards, and acronyms used across the whole bill of materials.",
    href: "/glossary",
    color: "text-indigo-500",
    available: true,
  },
  {
    icon: Globe,
    title: "Brand Portals",
    description: "Digital homes for the Future Manufacturing brand family — Sustainable Engineering Alliance, Disti, and Devkit each have their own portal.",
    href: "/brand/future-manufacturing",
    color: "text-teal-500",
    available: true,
  },
  {
    icon: Calendar,
    title: "Events",
    description: "Trade shows, webinars, conferences and roundtables — browse upcoming events and add them to your calendar.",
    available: false,
  },
  {
    icon: Mic,
    title: "Podcasts",
    description: "Audio conversations with the engineers, executives and innovators driving the industry forward.",
    available: false,
  },
  {
    icon: FileText,
    title: "Whitepapers & Reports",
    description: "In-depth technical papers, market research, and sponsored research reports from leading organisations.",
    available: false,
  },
  {
    icon: MonitorPlay,
    title: "Webinars",
    description: "On-demand and live webinars — register, watch, and earn CPD credits across a range of technical topics.",
    available: false,
  },
];

export default function ExplorePage() {
  const available = CONTENT_TYPES.filter(c => c.available) as ContentType[];
  const comingSoon = CONTENT_TYPES.filter(c => !c.available) as ComingSoon[];

  return (
    <>
      <Helmet>
        <title>What We Cover | Future Manufacturing</title>
        <meta
          name="description"
          content="Explore the full range of content on Future Manufacturing — news, video, magazine, products, companies, people, insights, glossary, and brand portals."
        />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-7xl px-4 pt-4 w-full">
          <PageBreadcrumb items={[{ label: "Explore" }]} />
        </div>

        <main className="flex-1">
          {/* Hero */}
          <section className="border-b bg-muted/30 py-14">
            <div className="mx-auto max-w-7xl px-4">
              <Badge variant="secondary" className="mb-4">What We Cover</Badge>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
                The whole BoM — in one place
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl">
                Future Manufacturing brings together every content type you need to stay on top of mechanical, electrical, and electronic engineering — today and as the platform grows.
              </p>
            </div>
          </section>

          {/* Live content types */}
          <section className="py-14">
            <div className="mx-auto max-w-7xl px-4">
              <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-8">
                Available now
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {available.map((ct) => {
                  const Icon = ct.icon;
                  return (
                    <Link key={ct.title} href={ct.href} data-testid={`card-content-type-${ct.title.toLowerCase().replace(/\s+/g, '-')}`}>
                      <div className="group h-full rounded-xl border bg-card p-6 hover:shadow-md transition-all hover:border-primary/40 cursor-pointer flex flex-col gap-4">
                        <div className={`w-10 h-10 rounded-lg bg-muted flex items-center justify-center ${ct.color} group-hover:scale-105 transition-transform`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-base mb-1 group-hover:text-primary transition-colors">{ct.title}</h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">{ct.description}</p>
                        </div>
                        <div className="flex items-center gap-1 text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                          Explore <ArrowRight className="h-3 w-3" />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Coming soon */}
          <section className="py-14 border-t bg-muted/20">
            <div className="mx-auto max-w-7xl px-4">
              <div className="flex items-center gap-3 mb-8">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
                  Coming soon
                </h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {comingSoon.map((ct) => {
                  const Icon = ct.icon;
                  return (
                    <div
                      key={ct.title}
                      data-testid={`card-coming-soon-${ct.title.toLowerCase().replace(/\s+/g, '-')}`}
                      className="rounded-xl border border-dashed bg-muted/30 p-6 flex flex-col gap-4 opacity-70"
                    >
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-base">{ct.title}</h3>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">Soon</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">{ct.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Subscribe CTA */}
          <section className="py-14 border-t">
            <div className="mx-auto max-w-7xl px-4 text-center">
              <h2 className="text-2xl font-bold mb-3">Never miss what's new</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Subscribe to get notified as new content types launch and stay ahead of the industry.
              </p>
              <Button asChild size="lg">
                <Link href="/subscribe">Subscribe Free <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </div>
          </section>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
